import {
  collectFieldResults,
  getNestedValue,
  isSectionOutOfScope,
  isSectionWithoutPresentValues,
} from "./analysisUtils";

export const DESIGN_SOLUTIONS = [
  {
    id: "ddos",
    label: "DDoS Design",
    path: ["design", "ddos"],
    testId: "design-solution-ddos",
  },
  {
    id: "appsec",
    label: "Cloud AppSec Design",
    path: ["design", "application_security"],
    testId: "design-solution-appsec",
  },
  {
    id: "adc",
    label: "ADC / On-Prem AppSec Design",
    path: ["design", "alteon"],
    testId: "design-solution-adc",
  },
  {
    id: "management",
    label: "Management Design",
    path: ["design", "global_management"],
    testId: "design-solution-management",
  },
];

const V3_SOLUTION_LABELS = {
  cloud_appsec: "Cloud AppSec",
  integrated_waf: "ADC / On-Prem AppSec Design",
  alteon: "ADC / On-Prem AppSec Design",
  onprem_appsec: "ADC / On-Prem AppSec Design",
};

export function v3SolutionDescriptor(instance) {
  return {
    id: `solution:${instance.id}`,
    label: V3_SOLUTION_LABELS[instance.type] || instance.type,
    testId: `design-solution-${instance.type}-${instance.id}`,
    instance,
  };
}

export const DDOS_PROJECT_TYPE = "design.ddos.project_type_cloud_onprem_hybrid";

export const DDOS_CLOUD_FIELDS = [
  "design.ddos.cloud.diversion_type",
  "design.ddos.cloud.mitigation_capacity",
  "design.ddos.cloud.legitimate_traffic_throughput",
  "design.ddos.cloud.customer_has_own_as",
  "design.ddos.cloud.number_of_networks_to_protect",
  "design.ddos.cloud.dns_diversion_assets",
  "design.ddos.cloud.dns_ddos_protection_required",
  "design.ddos.cloud.https_flood_protection_required",
  "design.ddos.cloud.management_visibility_features",
  "design.ddos.cloud.reporting_needs",
  "design.ddos.cloud.logs_needs",
  "design.ddos.cloud.storage_retention_needs",
];

export const DDOS_ONPREM_FIELDS = [
  "design.ddos.onprem.number_of_equipment",
  "design.ddos.onprem.ha_requirements",
  "design.ddos.onprem.deployment_mode",
  "design.ddos.onprem.mitigation_throughput",
  "design.ddos.onprem.legitimate_traffic_throughput",
  "design.ddos.onprem.interfaces_and_type",
  "design.ddos.onprem.power_supply",
  "design.ddos.onprem.bypass_requirements",
  "design.ddos.onprem.cps_pps_concurrent_connections",
  "design.ddos.onprem.dns_ddos_protection_required",
  "design.ddos.onprem.https_flood_protection_required",
  "design.ddos.onprem.management_visibility_features",
  "design.ddos.onprem.reporting_needs",
  "design.ddos.onprem.logs_needs",
  "design.ddos.onprem.storage_retention_needs",
];

export const APPSEC_FIELDS = [
  "design.application_security.deployment_model_cloud_onprem_both",
  "design.application_security.onprem_deployment_mode",
  "design.application_security.ha_requirements",
  "design.application_security.throughput_l4",
  "design.application_security.throughput_l7",
  "design.application_security.interfaces_and_type",
  "design.application_security.ram_disk",
  "design.application_security.power_supply",
  "design.application_security.cps_pps_concurrent_connections",
  "design.application_security.ssl_capacity",
  "design.application_security.number_of_applications",
  "design.application_security.number_of_apis",
  "design.application_security.api_discovery_required",
  "design.application_security.api_special_features",
  "design.application_security.bot_manager_required",
  "design.application_security.sophisticated_bot_requirement_1",
  "design.application_security.sophisticated_bot_requirement_2",
  "design.application_security.client_side_protection_required",
  "design.application_security.pci_certification_capabilities_required",
  "design.application_security.llm_firewall_required",
  "design.application_security.ddos_requested_and_capacity",
  "design.application_security.l7_ddos_requested",
  "design.application_security.management_visibility_features",
  "design.application_security.logs_needs",
  "design.application_security.storage_retention_needs",
  "design.application_security.reporting_needs",
  "design.application_security.ai_special_capabilities",
  "design.application_security.api_calls_or_requests_per_month",
  "design.application_security.monthly_traffic_volume_tb_gb",
  "design.application_security.total_http_https_requests_per_month",
];

// AppSec capabilities requested as an on-ADC module. These are intentionally
// shown under ADC Design without creating a separate AppSec product card.
export const ADC_EMBEDDED_APPSEC_FIELDS = [
  "design.application_security.number_of_applications",
  "design.application_security.number_of_apis",
  "design.application_security.api_discovery_required",
  "design.application_security.api_special_features",
  "design.application_security.bot_manager_required",
  "design.application_security.sophisticated_bot_requirement_1",
  "design.application_security.sophisticated_bot_requirement_2",
  "design.application_security.client_side_protection_required",
  "design.application_security.pci_certification_capabilities_required",
  "design.application_security.llm_firewall_required",
  "design.application_security.ddos_requested_and_capacity",
  "design.application_security.l7_ddos_requested",
  "design.application_security.ai_special_capabilities",
  "design.application_security.api_calls_or_requests_per_month",
  "design.application_security.monthly_traffic_volume_tb_gb",
  "design.application_security.total_http_https_requests_per_month",
];

// The dedicated Cloud AppSec card intentionally excludes appliance-specific
// capacity/deployment fields. CDN and DNS-as-a-Service belong here because
// they are cloud-service requirements.
export const CLOUD_APPSEC_FIELDS = [
  "design.application_security.number_of_applications",
  "design.application_security.number_of_apis",
  "design.application_security.api_discovery_required",
  "design.application_security.api_special_features",
  "design.application_security.bot_manager_required",
  "design.application_security.sophisticated_bot_requirement_1",
  "design.application_security.sophisticated_bot_requirement_2",
  "design.application_security.client_side_protection_required",
  "design.application_security.pci_certification_capabilities_required",
  "design.application_security.llm_firewall_required",
  "design.application_security.ddos_requested_and_capacity",
  "design.application_security.l7_ddos_requested",
  "design.application_security.management_visibility_features",
  "design.application_security.logs_needs",
  "design.application_security.storage_retention_needs",
  "design.application_security.reporting_needs",
  "design.application_security.ai_special_capabilities",
  "design.application_security.cdn_requirements",
  "design.application_security.dns_hosted_zones",
  "design.application_security.dns_queries_per_month",
  "design.application_security.api_calls_or_requests_per_month",
  "design.application_security.monthly_traffic_volume_tb_gb",
  "design.application_security.total_http_https_requests_per_month",
];

export const ADC_FIELDS = [
  "design.alteon.number_of_equipment",
  "design.alteon.ha_requirements",
  "design.alteon.throughput_l4",
  "design.alteon.throughput_l7",
  "design.alteon.interfaces_and_type",
  "design.alteon.ram_disk",
  "design.alteon.power_supply",
  "design.alteon.cps_pps_concurrent_connections",
  "design.alteon.ssl_capacity",
  "design.alteon.compression_throughput",
  "design.alteon.virtual_instances_partitions_route_domains",
  "design.alteon.security_features",
  "design.alteon.management_visibility_features",
  "design.alteon.reporting_needs",
  "design.alteon.logs_needs",
  "design.alteon.storage_retention_needs",
];

export const MANAGEMENT_FIELDS = [
  "design.global_management.centralized_management_required",
  "design.global_management.management_platform",
  "design.global_management.managed_technologies",
  "design.global_management.deployment_model",
  "design.global_management.ha_requirements",
  "design.global_management.visibility_features",
  "design.global_management.reporting_needs",
  "design.global_management.logs_needs",
  "design.global_management.storage_retention_needs",
  "design.global_management.event_correlation",
  "design.global_management.forensic_analysis",
  "design.global_management.rbac_or_user_roles",
  "design.global_management.audit_trail",
  "design.global_management.integrations",
  "design.global_management.admin_access_and_resilience",
  "design.global_management.licensing_or_scope_limits",
];

export const MANAGEMENT_DETECTION_FIELDS = [
  "design.global_management.detection.flows_per_second",
  "design.global_management.detection.routers_to_monitor",
  "design.global_management.detection.flow_protocols",
  "design.global_management.detection.prefixes_and_objects",
  "design.global_management.detection.traffic_visibility_and_analytics",
  "design.global_management.detection.diversion_methods",
];

const DDOS_TECH_PATTERNS = [/defensepro/i, /cloud ddos/i, /on-premise ddos/i];
const ADC_TECH_PATTERNS = [/alteon/i, /\badc\b/i];
const CYBER_CONTROLLER_PATTERNS = [/cyber controller/i, /centralized management/i];

function technologiesList(analysis) {
  const tech = analysis?.general?.technologies_involved;
  if (tech?.status === "found" && Array.isArray(tech.value)) return tech.value;
  return [];
}

export function getProductPresence(analysis) {
  if (analysis?.product_presence && typeof analysis.product_presence === "object") {
    return analysis.product_presence;
  }
  const techs = technologiesList(analysis);
  const has = (re) => techs.some((t) => re.test(t));
  return {
    ddos_onprem: has(/defensepro|on-premise ddos/i),
    ddos_cloud: has(/cloud ddos/i),
    alteon: has(/alteon|\badc\b/i),
    application_security: has(/waf|api security|appsec|application security/i),
    cyber_controller: has(/cyber controller|centralized management/i),
  };
}

export function isAppSecTechnologyInScope(analysis) {
  const presence = getProductPresence(analysis);
  if (presence.cloud_waf) return true;
  return technologiesList(analysis).some((t) => /cloud\s+(?:waf|appsec)/i.test(t));
}

export function isDdosTechnologyInScope(analysis) {
  const presence = getProductPresence(analysis);
  if (presence.ddos_onprem || presence.ddos_cloud) return true;
  return technologiesList(analysis).some((t) => DDOS_TECH_PATTERNS.some((re) => re.test(t)));
}

export function isAdcTechnologyInScope(analysis) {
  const presence = getProductPresence(analysis);
  if (presence.alteon || presence.integrated_waf || presence.adc_embedded_appsec) return true;
  return technologiesList(analysis).some((t) => ADC_TECH_PATTERNS.some((re) => re.test(t)));
}

export function isManagementDesignInScope(analysis) {
  if (analysis?.global_management_applicable === true) return true;
  const presence = getProductPresence(analysis);
  if (presence.cyber_controller) return true;

  const gmNode = analysis?.design?.global_management;
  if (!gmNode) return false;

  const fields = collectFieldResults(gmNode, "design.global_management");
  if (fields.some(({ result }) => result?.status === "found")) return true;

  const central = getNestedValue(analysis, [
    "design",
    "global_management",
    "centralized_management_required",
  ]);
  return central?.status === "found";
}

function solutionTechnologyInScope(analysis, solution) {
  if (solution.instance) return solution.instance.procurement_status === "requested";
  switch (solution.id) {
    case "ddos":
      return isDdosTechnologyInScope(analysis);
    case "adc":
      return isAdcTechnologyInScope(analysis);
    case "appsec":
      return isAppSecTechnologyInScope(analysis);
    case "management":
      return isManagementDesignInScope(analysis);
    default:
      return true;
  }
}

export function adcInstanceDisplayName(instance, allInstances = []) {
  const name = instance?.name || instance?.instance_id || "ADC instance";
  if (/generic/i.test(name) || instance?.role === "generic_adc") {
    return null;
  }
  const role = instance?.role;
  const sameRole = (allInstances || []).filter((i) => i.role === role);
  if (sameRole.length <= 1 && /\s+\d+$/.test(name)) {
    return name.replace(/\s+\d+$/, "");
  }
  return name;
}

export function adcInstanceCardSummary(instance) {
  const fields = instance?.fields || {};
  const parts = [];
  const equipment = adcInstanceFieldValue(fields, "number_of_equipment");
  if (equipment) {
    parts.push(`${equipment} appliance${equipment === "1" ? "" : "s"}`);
  }
  const ha = fields?.ha_requirements;
  if (ha?.status === "found" && ha.value) {
    parts.push("HA");
  }
  const l4 = adcInstanceFieldValue(fields, "throughput_l4");
  const l7 = adcInstanceFieldValue(fields, "throughput_l7");
  const sizingParts = [l4 ? `${l4} L4` : null, l7 ? `${l7} L7` : null].filter(Boolean);
  if (sizingParts.length) {
    parts.push(sizingParts.join(" · "));
  }
  return parts.join(" · ") || "No summary";
}

export function visibleAdcInstances(analysis) {
  const instances = analysis?.design?.adc?.instances || [];
  return instances.filter((inst) => {
    const name = inst?.name || "";
    return inst?.role !== "generic_adc" && !/generic adc/i.test(name);
  });
}

export function hasAdcInstances(analysis) {
  return visibleAdcInstances(analysis).length > 0;
}

export function adcInstanceSummaryText(analysis) {
  const summary = analysis?.design?.adc?.summary;
  if (summary?.status !== "found" || !summary.value) return null;
  const { total_equipment: totalEquipment, ha_pairs: haPairs } = summary.value;
  const parts = [];
  if (totalEquipment != null && totalEquipment > 0) {
    parts.push(`${totalEquipment} appliance${totalEquipment === 1 ? "" : "s"}`);
  }
  if (haPairs != null && haPairs > 0) {
    parts.push(`${haPairs} HA pair${haPairs === 1 ? "" : "s"}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

export const ADC_INSTANCE_FIELD_NAMES = [
  "number_of_equipment",
  "ha_requirements",
  "deployment_mode",
  "site_distribution",
  "throughput_l4",
  "throughput_l7",
  "interfaces_and_type",
  "ram_disk",
  "power_supply",
  "cps_pps_concurrent_connections",
  "ssl_capacity",
  "compression_throughput",
  "virtual_instances_partitions_route_domains",
  "management_visibility_features",
  "reporting_needs",
  "logs_needs",
  "storage_retention_needs",
  "key_features",
];

export const ADC_INSTANCE_FIELD_LABELS = {
  number_of_equipment: "Number of equipment",
  ha_requirements: "HA requirements",
  deployment_mode: "Deployment mode",
  site_distribution: "Site distribution",
  throughput_l4: "Throughput L4",
  throughput_l7: "Throughput L7",
  interfaces_and_type: "Interfaces and type",
  ram_disk: "RAM / Disk",
  power_supply: "Power supply",
  cps_pps_concurrent_connections: "CPS / PPS / Concurrent connections",
  ssl_capacity: "SSL capacity",
  compression_throughput: "Compression throughput",
  virtual_instances_partitions_route_domains: "Virtual partitions",
  management_visibility_features: "Management visibility",
  reporting_needs: "Reporting needs",
  logs_needs: "Logs needs",
  storage_retention_needs: "Storage / retention",
  key_features: "Key features",
};

export const ADC_INSTANCE_FIELD_LABELS_ES = {
  compression_throughput: "Throughput de Compresión",
  virtual_instances_partitions_route_domains: "Número de instancias virtuales",
  power_supply: "Fuente de poder",
};

export function adcInstanceFieldLabel(fieldName, language = "en") {
  if (language === "es" && ADC_INSTANCE_FIELD_LABELS_ES[fieldName]) {
    return ADC_INSTANCE_FIELD_LABELS_ES[fieldName];
  }
  return ADC_INSTANCE_FIELD_LABELS[fieldName] || fieldName.replace(/_/g, " ");
}

export function adcInstanceFieldValue(fields, fieldName) {
  const result = fields?.[fieldName];
  if (!result || result.status !== "found" || result.value == null) return null;
  if (Array.isArray(result.value)) return result.value.join("; ");
  return String(result.value);
}

export function solutionFieldResults(analysis, solution) {
  if (solution.instance) {
    const fields = [];
    for (const [group, node] of Object.entries(solution.instance.fields || {})) {
      const prefix = group === "alteon" ? "design.alteon" : "design.application_security";
      fields.push(...collectFieldResults(node, prefix));
    }
    return fields;
  }
  if (solution.id === "adc" && hasAdcInstances(analysis)) {
    const adc = analysis.design.adc;
    const instances = visibleAdcInstances(analysis);
    const fields = [];
    if (adc.summary?.status) {
      fields.push({ fieldId: "design.adc.summary", result: adc.summary });
    }
    instances.forEach((inst, index) => {
      fields.push({
        fieldId: `design.adc.instances.${index}`,
        result: { status: "found", value: inst.name || inst.instance_id },
      });
    });
    return fields;
  }
  if (solution.id === "adc") {
    const fields = collectFieldResults(analysis?.design?.alteon, "design.alteon");
    const presence = getProductPresence(analysis);
    // An Integrated/on-prem WAF has no separate Alteon requirement set. Its
    // requirements live under application_security, so include them when
    // deciding whether the combined card is in scope.
    if (presence.integrated_waf || presence.adc_embedded_appsec) {
      fields.push(...collectFieldResults(analysis?.design?.application_security, "design.application_security"));
    }
    return fields;
  }
  const node = getNestedValue(analysis, solution.path);
  const prefix = solution.path.join(".");
  return collectFieldResults(node, prefix);
}

export function isSolutionOutOfScope(analysis, solution) {
  if (solution.instance) return solution.instance.procurement_status !== "requested";
  const fields = solutionFieldResults(analysis, solution);
  if (!fields.length) return true;
  // Management Design: hide the empty wall of not_found cards even when
  // Cyber Controller was inferred. Other solutions stay visible if any field
  // is not_found (in-scope but missing), and hide only when all not_applicable.
  if (solution.id === "management") {
    return isSectionWithoutPresentValues(fields);
  }
  return isSectionOutOfScope(fields);
}

export function shouldIncludeSolutionInPicker(analysis, solution, showOutOfScope = false) {
  const fields = solutionFieldResults(analysis, solution);
  if (!fields.length) return false;

  const techInScope = solutionTechnologyInScope(analysis, solution);
  const oos = isSolutionOutOfScope(analysis, solution);

  if (!techInScope) {
    if (solution.id === "management") return false;
    return showOutOfScope && oos;
  }
  if (oos && !showOutOfScope) return false;
  return true;
}

export function visibleDesignSolutions(analysis, showOutOfScope) {
  if (Array.isArray(analysis?.design?.solutions)) {
    const stable = DESIGN_SOLUTIONS.filter((solution) => ["ddos", "management"].includes(solution.id)).filter((solution) =>
      shouldIncludeSolutionInPicker(analysis, solution, showOutOfScope)
    );
    const requestedInstances = analysis.design.solutions
      .filter((instance) => showOutOfScope || instance.procurement_status === "requested");
    const onPremInstances = requestedInstances.filter((instance) => ["integrated_waf", "alteon"].includes(instance.type));
    const otherInstances = requestedInstances.filter((instance) => !["integrated_waf", "alteon"].includes(instance.type));
    const dynamic = otherInstances
      .map(v3SolutionDescriptor)
      .concat(onPremInstances.length ? [v3SolutionDescriptor({
        id: "onprem-appsec",
        type: "onprem_appsec",
        procurement_status: "requested",
        products: [...new Set(onPremInstances.flatMap((instance) => instance.products || []))],
        components: [...new Set(onPremInstances.flatMap((instance) => instance.components || []))],
        fields: onPremInstances.reduce((combined, instance) => ({
          application_security: { ...combined.application_security, ...(instance.fields?.application_security || {}) },
          alteon: { ...combined.alteon, ...(instance.fields?.alteon || {}) },
        }), { application_security: {}, alteon: {} }),
        evidence_row_ids: [...new Set(onPremInstances.flatMap((instance) => instance.evidence_row_ids || []))],
      })] : []);
    return [stable.find((item) => item.id === "ddos"), ...dynamic, stable.find((item) => item.id === "management")].filter(Boolean);
  }
  return DESIGN_SOLUTIONS.filter((solution) =>
    shouldIncludeSolutionInPicker(analysis, solution, showOutOfScope)
  );
}

export function hiddenDesignSolutionNames(analysis, showOutOfScope) {
  if (Array.isArray(analysis?.design?.solutions)) {
    if (showOutOfScope) return [];
    return analysis.design.solutions
      .filter((instance) => instance.procurement_status !== "requested")
      .map((instance) => V3_SOLUTION_LABELS[instance.type] || instance.type);
  }
  if (showOutOfScope) return [];
  return DESIGN_SOLUTIONS.filter((solution) => {
    const fields = solutionFieldResults(analysis, solution);
    if (!fields.length) return false;
    const techInScope = solutionTechnologyInScope(analysis, solution);
    const oos = isSolutionOutOfScope(analysis, solution);
    if (!techInScope && solution.id === "management") return false;
    if (!techInScope && oos) return true;
    if (techInScope && oos) return true;
    return false;
  }).map((s) => s.label);
}

export function isSolutionApplicable(analysis, solution) {
  return shouldIncludeSolutionInPicker(analysis, solution, false);
}

function fieldAt(analysis, fieldId) {
  return getNestedValue(analysis, fieldId.split("."));
}

function shortValue(result) {
  if (!result || result.status !== "found" || result.value == null) return null;
  if (Array.isArray(result.value)) return result.value.join(", ");
  return String(result.value);
}

function countFoundMissing(analysis, fieldIds) {
  let found = 0;
  let missing = 0;
  for (const fieldId of fieldIds) {
    const result = fieldAt(analysis, fieldId);
    if (!result) continue;
    if (result.status === "found") found += 1;
    if (result.status === "not_found") missing += 1;
  }
  return { found, missing };
}

export function buildSolutionCardSummary(analysis, solution) {
  const oos = isSolutionOutOfScope(analysis, solution);
  if (solution.instance) {
    const instance = solution.instance;
    return {
      chips: [
        ...(instance.products || []),
        instance.deployment && instance.deployment !== "unknown" ? `Deployment: ${instance.deployment}` : null,
        instance.equipment?.ha_pairs ? `HA pairs: ${instance.equipment.ha_pairs}` : null,
        instance.equipment?.quantity ? `Appliances: ${instance.equipment.quantity}` : null,
        instance.review_required ? "Review required" : null,
      ].filter(Boolean),
      muted: oos,
    };
  }
  if (solution.id === "ddos") {
    const projectType = shortValue(fieldAt(analysis, DDOS_PROJECT_TYPE));
    const counts = countFoundMissing(analysis, [...DDOS_CLOUD_FIELDS, ...DDOS_ONPREM_FIELDS, DDOS_PROJECT_TYPE]);
    return {
      chips: [
        projectType ? `Project type: ${projectType}` : null,
        `Found: ${counts.found}`,
        counts.missing ? `Missing: ${counts.missing}` : null,
      ].filter(Boolean),
      muted: oos,
    };
  }
  if (solution.id === "appsec") {
    const deployment = shortValue(fieldAt(analysis, "design.application_security.deployment_model_cloud_onprem_both"));
    const apps = shortValue(fieldAt(analysis, "design.application_security.number_of_applications"));
    const apis = shortValue(fieldAt(analysis, "design.application_security.number_of_apis"));
    return {
      chips: [
        deployment ? `Deployment: ${deployment}` : null,
        apps ? `Applications: ${apps}` : null,
        apis ? `APIs: ${apis}` : null,
      ].filter(Boolean),
      muted: oos,
    };
  }
  if (solution.id === "adc") {
    if (hasAdcInstances(analysis)) {
      const summaryText = adcInstanceSummaryText(analysis);
      return {
        chips: [summaryText || "Multiple ADC instances"].filter(Boolean),
        muted: oos,
      };
    }
    const equipment = shortValue(fieldAt(analysis, "design.alteon.number_of_equipment"));
    return {
      chips: [
        oos ? "Out of scope" : "Applicable",
        equipment ? `Equipment: ${equipment}` : null,
      ].filter(Boolean),
      muted: oos,
    };
  }
  if (solution.id === "management") {
    const central = shortValue(fieldAt(analysis, "design.global_management.centralized_management_required"));
    const managed = shortValue(fieldAt(analysis, "design.global_management.managed_technologies"));
    const coverage = countFoundMissing(analysis, [
      "design.global_management.reporting_needs",
      "design.global_management.logs_needs",
      "design.global_management.storage_retention_needs",
    ]);
    return {
      chips: [
        central ? `Centralized: ${central}` : null,
        managed ? `Managed: ${managed}` : null,
        coverage.found ? `Reporting/logs/retention: ${coverage.found}/3` : null,
      ].filter(Boolean),
      muted: oos,
    };
  }
  return { chips: [], muted: oos };
}

export function subsectionHasUsefulContent(analysis, path) {
  const node = getNestedValue(analysis, path);
  const fields = collectFieldResults(node, path.join("."));
  return fields.some(({ result }) => result?.status === "found" || result?.status === "not_found");
}

export function hasGlobalManagementCoverage(analysis, fieldIds) {
  return fieldIds.some((fieldId) => {
    const result = fieldAt(analysis, fieldId);
    return result?.covered_by_global_management && result?.coverage_source !== "native";
  });
}
